import React, { useState, useEffect, useRef } from 'react';
import { Facebook, Instagram, ExternalLink, Heart, MessageCircle, Play, Camera, MapPin, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Instagram-style image card
const InstaCard = ({ image, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <a
      href="https://www.instagram.com/hotelumapremium/"
      target="_blank"
      rel="noopener noreferrer"
      className="relative aspect-square overflow-hidden rounded-lg group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`insta-card-${index}`}
    >
      <img
        src={image}
        alt={`Hotel Uma Premium Instagram ${index + 1}`}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className={`absolute inset-0 bg-black/40 flex items-center justify-center gap-4 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        <div className="flex items-center gap-1.5 text-white">
          <Heart className="w-5 h-5 fill-white" />
          <span className="font-semibold text-sm">{Math.floor(Math.random() * 80 + 20)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-white">
          <MessageCircle className="w-5 h-5 fill-white" />
          <span className="font-semibold text-sm">{Math.floor(Math.random() * 15 + 2)}</span>
        </div>
      </div>
    </a>
  );
};

const SocialFeeds = () => {
  const { t } = useLanguage();
  const [previewImages, setPreviewImages] = useState([]);
  const fbRef = useRef(null);

  // Fetch hotel images for Instagram-style grid
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/gallery`);
        if (response.data && response.data.length > 0) {
          // Pick 6 varied images from different categories
          const categories = [...new Set(response.data.map(i => i.category))];
          const selected = [];
          for (const cat of categories) {
            const catImages = response.data.filter(i => i.category === cat);
            if (catImages.length > 0 && selected.length < 6) {
              selected.push(catImages[0].image);
            }
          }
          // Fill remaining with first available
          while (selected.length < 6 && response.data.length > selected.length) {
            const img = response.data[selected.length]?.image;
            if (img && !selected.includes(img)) selected.push(img);
            else break;
          }
          setPreviewImages(selected);
        }
      } catch {
        // Fallback images
        setPreviewImages([
          "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80",
          "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80",
          "https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80",
          "https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800&q=80",
          "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&q=80",
          "https://customer-assets.emergentagent.com/job_cf14decb-7e72-4ae9-98ca-c0ee15b9566b/artifacts/t7df7qhw_588A2751.JPG",
        ]);
      }
    };
    fetchImages();
  }, []);

  return (
    <section id="social" className="py-20 bg-gradient-to-b from-white to-slate-50" data-testid="social-feeds-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">
            {t('social.subtitle')}
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-800 mt-3 mb-4">
            {t('social.title')}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t('social.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Instagram Feed Preview */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="instagram-feed">
            {/* Instagram Header */}
            <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">@hotelumapremium</h3>
                    <p className="text-white/80 text-sm">Instagram</p>
                  </div>
                </div>
                <a
                  href="https://www.instagram.com/hotelumapremium/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/30 transition-colors flex items-center gap-1.5"
                  data-testid="instagram-follow-btn"
                >
                  <Heart className="w-4 h-4" />
                  {t('social.followInstagram')}
                </a>
              </div>
              {/* Stats bar */}
              <div className="flex gap-6 mt-4 text-white/90 text-sm">
                <div className="flex items-center gap-1.5">
                  <Camera className="w-4 h-4" />
                  <span className="font-semibold">50+</span> posts
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  <span className="font-semibold">1K+</span> followers
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  Ulcinj, Montenegro
                </div>
              </div>
            </div>

            {/* Instagram-style Image Grid */}
            <div className="p-3">
              <div className="grid grid-cols-3 gap-1.5">
                {previewImages.map((img, index) => (
                  <InstaCard key={img} image={img} index={index} />
                ))}
              </div>
              
              {/* View Profile CTA */}
              <a
                href="https://www.instagram.com/hotelumapremium/"
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4 text-center py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
                data-testid="instagram-view-profile-btn"
              >
                {t('social.viewInstagramProfile') || 'View Full Profile on Instagram'}
              </a>
            </div>
          </div>

          {/* Right Column: Facebook + TikTok */}
          <div className="flex flex-col gap-6">
            {/* Facebook Page Plugin */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex-1" data-testid="facebook-feed">
              {/* Facebook Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <Facebook className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Hotel Uma Premium Ulcinj</h3>
                      <p className="text-white/80 text-sm">Facebook</p>
                    </div>
                  </div>
                  <a
                    href="https://www.facebook.com/umapremium/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/30 transition-colors flex items-center gap-1.5"
                    data-testid="facebook-follow-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('social.followFacebook')}
                  </a>
                </div>
              </div>

              {/* Facebook Embed */}
              <div className="p-4" ref={fbRef}>
                <div className="overflow-hidden rounded-xl bg-slate-50">
                  <iframe
                    src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fumapremium%2F&tabs=timeline&width=500&height=300&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                    width="100%"
                    height="300"
                    style={{ border: 'none', overflow: 'hidden' }}
                    scrolling="no"
                    frameBorder="0"
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    title="Hotel Uma Premium Facebook Page"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3 flex gap-3 text-sm text-slate-500">
                  <span className="font-semibold text-slate-700">500+</span> likes
                  <span className="text-slate-300">|</span>
                  <span className="font-semibold text-slate-700">4.8</span> rating
                </div>
              </div>
            </div>

            {/* TikTok Card */}
            <a
              href="https://www.tiktok.com/@umapremium"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1"
              data-testid="social-card-tiktok"
            >
              <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center relative">
                      <TikTokIcon className="w-7 h-7 text-white" />
                      {/* TikTok color accents */}
                      <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-cyan-400 rounded-full"></div>
                      <div className="absolute -bottom-0.5 -left-0.5 w-3 h-3 bg-rose-500 rounded-full"></div>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">@umapremium</h3>
                      <p className="text-white/70 text-sm">TikTok</p>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                </div>
              </div>
              <div className="p-5">
                <p className="text-slate-600 mb-4">
                  {t('social.tiktokDesc')}
                </p>
                <div className="flex items-center gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-800 font-semibold">20+</span>
                    <span className="text-slate-500">videos</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-800 font-semibold">10K+</span>
                    <span className="text-slate-500">views</span>
                  </div>
                </div>
                <div className="w-full py-3 rounded-xl text-center font-semibold text-white bg-gradient-to-r from-gray-900 to-gray-800 group-hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                  <Play className="w-4 h-4" />
                  {t('social.followTiktok')}
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-500 mb-4">{t('social.shareExperience')}</p>
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-5 py-2 rounded-full font-medium">
            <span>#</span>
            <span>UmaPremium</span>
            <span className="text-amber-500">|</span>
            <span>#UlcinjMontenegro</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialFeeds;
